import { PuckContext } from '@measured/puck';

import { AccordianBlock, AccordianBlockProps } from '../../shared/blocks/accordian/accordian';
import { ListBlock, ListBlockProps } from '../../shared/blocks/list/list';
import { TextBlock, TextBlockProps } from '../../shared/blocks/text/text';
import { HeaderBlock, HeaderBlockProps } from '../blocks/header/header';
import { ContentPageMetadata } from '../types';

import { parseTextWithTokens } from './token-replacement';

type BlockWithRender<T> = {
  render: (props: T & { puck: PuckContext; id: string }) => React.ReactNode;
};

function createProxiedBlock<TBlock extends BlockWithRender<TProps>, TProps>(
  block: TBlock,
  transformProps: (props: TProps, metadata: ContentPageMetadata) => TProps
) {
  return (metadata: ContentPageMetadata) => ({
    ...block,
    render: new Proxy(block.render, {
      apply(target, thisArg, argumentsList) {
        const [props] = argumentsList as [TProps];
        const transformedProps = transformProps(props, metadata);
        return Reflect.apply(target, thisArg, [transformedProps]);
      },
    }),
  });
}

const createProxiedAccordianBlock = createProxiedBlock<typeof AccordianBlock, AccordianBlockProps>(
  AccordianBlock,
  (props, metadata) => ({
    ...props,
    items: props.items.map(({ item }) => ({
      item: {
        title: item.title,
        content: typeof item.content === 'string' ? parseTextWithTokens(item.content, metadata) : item.content,
      },
    })),
  })
);

const createProxiedTextBlock = createProxiedBlock<typeof TextBlock, TextBlockProps>(TextBlock, (props, metadata) => ({
  ...props,
  text: typeof props.text === 'string' ? parseTextWithTokens(props.text, metadata) : props.text,
}));

const createProxiedHeaderBlock = createProxiedBlock<typeof HeaderBlock, HeaderBlockProps>(
  HeaderBlock,
  (props, metadata) => ({
    ...props,
    text: typeof props.text === 'string' ? parseTextWithTokens(props.text, metadata) : props.text,
  })
);

const createProxiedListBlock = createProxiedBlock<typeof ListBlock, ListBlockProps>(ListBlock, (props, metadata) => ({
  ...props,
  text: props.text.map((t) => ({
    item: t.item ? parseTextWithTokens(t.item as string, metadata) : undefined,
  })),
}));

export { createProxiedAccordianBlock, createProxiedHeaderBlock, createProxiedListBlock, createProxiedTextBlock };
